"use client";

import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import BookingConfirmationCard from "./BookingConfirmationCard";
import SchedulerMobileDrawer from "./SchedulerMobileDrawer";
import SchedulerSlot from "./SchedulerSlot";
import "./scheduler-screen.css";

interface Props {
  id: string;
  step: FormJourneyItem;
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
  handleAnswer?: () => void;
  footer?: ReactNode;
  /** Nested inside a multi-question page — stack label + widget with content-driven height. */
  embeddedInline?: boolean;
}

type BookingValue = {
  startTime: string;
  endTime: string;
  inviteeName: string;
  inviteeEmail: string;
  externalId: string;
  externalUrl: string | null;
  meetUrl: string | null;
};

const MOBILE_LAYOUT_BREAKPOINT = 767;

function parseBookingValue(raw: unknown): BookingValue | null {
  if (typeof raw !== "string" || raw.length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<BookingValue>;
    if (!parsed.startTime || !parsed.endTime || !parsed.externalId) return null;
    return {
      startTime: parsed.startTime,
      endTime: parsed.endTime,
      inviteeName: parsed.inviteeName ?? "",
      inviteeEmail: parsed.inviteeEmail ?? "",
      externalId: parsed.externalId,
      externalUrl: parsed.externalUrl ?? null,
      meetUrl: parsed.meetUrl ?? null,
    };
  } catch {
    return null;
  }
}

const SchedulerScreen = ({
  id,
  step,
  leadForm,
  editorMode,
  previewMode,
  active,
  onContentClick,
  hiddenFields,
  handleAnswer,
  footer,
  embeddedInline = false,
}: Props) => {
  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues();
  const watchedBookingRaw = form.watch(step.blockId);
  const currentResponseScore = userResponses ? getCurrentResponseScore(userResponses, leadForm) : 0;
  const screenRef = useRef<HTMLDivElement>(null);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const booking = useMemo(() => parseBookingValue(watchedBookingRaw), [watchedBookingRaw]);
  const hasBooking = !!booking;

  useEffect(() => {
    const node = screenRef.current;
    if (!node) return;
    const wrapper = node.closest<HTMLElement>(".conversationWrapper");
    const target = wrapper ?? node;

    const updateLayout = () => {
      setIsMobileLayout(target.getBoundingClientRect().width <= MOBILE_LAYOUT_BREAKPOINT);
    };

    updateLayout();
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(target);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (hasBooking && drawerOpen) setDrawerOpen(false);
  }, [hasBooking, drawerOpen]);

  useEffect(() => {
    if (!isMobileLayout && drawerOpen) setDrawerOpen(false);
  }, [isMobileLayout, drawerOpen]);

  const handleContentClick = (field: "content" | "secondaryContent") => {
    if (!editorMode || (editorMode && previewMode) || !onContentClick) return;
    onContentClick(field, id);
  };

  const schedulerSlot = (
    <SchedulerSlot
      step={step}
      editorMode={editorMode}
      previewMode={previewMode}
      active={active}
      handleAnswer={handleAnswer}
      embeddedInline={embeddedInline}
    />
  );

  return (
    <>
      <div
        ref={screenRef}
        className={cn(
          "questionInner",
          "schedulerScreen",
          previewMode && "schedulerScreen--preview",
          embeddedInline && "schedulerScreen--inline",
          isMobileLayout && !previewMode && !embeddedInline && "schedulerScreen--mobile",
        )}
      >
        <div className="schedulerScreen__main">
          <div className="schedulerScreen__copy">
            <h4
              onClick={() => handleContentClick("content")}
              className={cn(hasBooking && "semiFade", "question")}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.content,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode: previewMode ?? false,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses,
                  }),
                }}
              />
            </h4>
            {step.secondaryContent && (
              <p
                onClick={() => handleContentClick("secondaryContent")}
                className={cn(hasBooking && "semiFade", "caption")}
              >
                <motion.span
                  dangerouslySetInnerHTML={{
                    __html: dynamicQuestionContent({
                      content: step.secondaryContent,
                      score: currentResponseScore,
                      editMode: editorMode,
                      previewMode: previewMode ?? false,
                      hiddenFields,
                      journeyItems: leadForm.journeyItems ?? [],
                      userResponses,
                    }),
                  }}
                />
              </p>
            )}
          </div>

          {isMobileLayout && !previewMode && !embeddedInline && (
            <div className="answerInputWrapper schedulerScreen__mobileActions">
              {hasBooking && booking ? (
                <BookingConfirmationCard
                  startTime={booking.startTime}
                  endTime={booking.endTime}
                  inviteeName={booking.inviteeName}
                  inviteeEmail={booking.inviteeEmail}
                  meetUrl={booking.meetUrl}
                  externalUrl={booking.externalUrl}
                  isPending={booking.externalId.startsWith("pending:")}
                />
              ) : (
                <button
                  type="button"
                  className="schedulerScreen__mobileTrigger"
                  onClick={() => setDrawerOpen(true)}
                >
                  Schedule time
                </button>
              )}
            </div>
          )}

          {footer ? <div className="footerWrapper">{footer}</div> : null}
        </div>

        {(!isMobileLayout || previewMode || embeddedInline) && (
          <div className="schedulerScreen__widget schedulerScreen__widget--desktop">
            <motion.div key={`${id}-scheduler-widget`} className="answerInputWrapper schedulerWidgetWrapper">
              {schedulerSlot}
            </motion.div>
          </div>
        )}
      </div>

      {isMobileLayout && !previewMode && !embeddedInline && (
        <SchedulerMobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          {schedulerSlot}
        </SchedulerMobileDrawer>
      )}
    </>
  );
};

export default SchedulerScreen;
